import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
  Box,
  Typography,
} from '@mui/material';
import CbButton from '../../../../components/Buttons/CbButton';
import TextFieldBase from '../../../../components/inputs/text-fields/base/TextFieldBase';
import { reopenSubmission } from '../../../../api/submissions/submissions.api';
import useNotifications from '../../../../_lib/notifications/useNotifications';

const reopenReasons = [
  { label: 'Sent to Carrier by Mistake', value: 'SENT_TO_CARRIER_BY_MISTAKE' },
  { label: 'Carrier provided Exception', value: 'CARRIER_PROVIDED_EXCEPTION' },
  { label: 'Other', value: 'OTHER' },
];

const schema = Yup.object().shape({
  reopenReason: Yup.string().required('Re-Open reason is required'),
  reopenReasonDescription: Yup.string().required('Re-Open note is required'),
});

const ReopenSubmissionModal = ({
  data,
  close,
  title,
}: {
  data: {
    submissionId: string;
    accountId: string;
    submissionNumber: string;
    accountName: string;
  };
  close: () => void;
  title?: string;
}) => {
  const notifications = useNotifications();
  const methods = useForm({
    defaultValues: {
      reopenReason: '',
      reopenReasonDescription: '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (formData: any) => {
    try {
      await reopenSubmission(data.submissionId, {
        data: {
          reOpenReason: formData.reopenReason,
          reOpenReasonDescription: formData.reopenReasonDescription,
        },
      });
      notifications.success('Submission re-opened successfully.');
      close();
    } catch (err) {
      notifications.error('Unable to re-open submission.');
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={3} mt={2} textAlign="center">
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '1.1rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              mb: 2,
            }}
          >
            Are you sure you want to re-open this submission?
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.35rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              letterSpacing: 0.5,
              textAlign: 'center',
            }}
          >
            {data.submissionNumber}: {data.accountName}
          </Typography>
        </Box>
        <Box style={{ padding: '0 24px' }}>
          <Box mb={2}>
            <InputLabel required id="reopen-reason-label">
              Re-Open Reason
            </InputLabel>
            <Select
              labelId="reopen-reason-label"
              fullWidth
              {...register('reopenReason')}
              defaultValue=""
              error={!!errors.reopenReason}
            >
              {reopenReasons.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.reopenReason && (
              <FormHelperText error>
                {errors.reopenReason.message}
              </FormHelperText>
            )}
          </Box>
          <Box mb={2}>
            <TextFieldBase
              label="Re-Open Note"
              required
              multiline
              minRows={4}
              errorMessage={errors.reopenReasonDescription?.message}
              inputProps={{ maxLength: 500 }}
              {...register('reopenReasonDescription')}
            />
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={2} pb={2}>
            <CbButton
              styleName="cancel"
              onClick={close}
              buttonText="Cancel"
              disabled={isSubmitting}
            />
            <CbButton
              styleName="ctaButton"
              type="submit"
              buttonText="Re-Open Submission"
              loading={isSubmitting}
            />
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default ReopenSubmissionModal;
